// src/pages/shipping/SurchargeTable.tsx
/* Movatic WMS — Frontend | CesarFilho 932048016
   CRUD de sobretaxas de uma RateTable (por tableId).
*/
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
    Button,
    Input,
    Label,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui";

type Surcharge = {
    id: number;
    rate_table: number;
    name: string; // ex: "Combustível", "Expedição", "Por distância"
    kind?: string | null; // livre: "percent" | "fixed" | "per_kg" | ...
    value?: string | null; // valor principal (percentual / fixo / coef.)
    min_value?: string | null; // piso
    max_value?: string | null; // teto
};

type Paged<T> = { count: number; next: string | null; previous: string | null; results: T[] };
const PAGE_SIZE = 10;

export default function SurchargeTable({ tableId }: { tableId: number }) {
    const qc = useQueryClient();
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState<Surcharge | null>(null);

    const qRows = useQuery<Paged<Surcharge>>({
        queryKey: ["surcharges", { tableId, page }],
        queryFn: async () => {
            const { data } = await api.get(`/api/shipping/rate-tables/${tableId}/surcharges/`, {
                params: { page, page_size: PAGE_SIZE },
            });
            if (Array.isArray(data)) {
                return { count: data.length, next: null, previous: null, results: data as Surcharge[] };
            }
            return data as Paged<Surcharge>;
        },
        // tipado para evitar TS7006
        placeholderData: (p: Paged<Surcharge> | undefined) => p,
        staleTime: 10_000,
    });

    useEffect(() => {
        setPage(1);
    }, [tableId]);

    const total: number = qRows.data?.count ?? 0;
    const items: Surcharge[] = qRows.data?.results ?? [];
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const mCreate = useMutation({
        mutationFn: async (payload: Partial<Surcharge>) =>
            api.post(`/api/shipping/rate-tables/${tableId}/surcharges/`, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["surcharges"] });
            setOpen(false);
        },
    });

    const mUpdate = useMutation({
        mutationFn: async () => {
            if (!editing) return;
            return api.patch(`/api/shipping/surcharges/${editing.id}/`, editing);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["surcharges"] });
            setEditing(null);
        },
    });

    const mDelete = useMutation({
        mutationFn: async (id: number) => api.delete(`/api/shipping/surcharges/${id}/`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["surcharges"] }),
    });

    return (
        <div className="p-3 space-y-3">
            <div className="flex items-center justify-between">
                <div className="font-semibold">Regras / Sobretaxas</div>
                <Button size="sm" onClick={() => setOpen(true)}>
                    + Adicionar
                </Button>
            </div>

            <div className="overflow-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-900/30">
                        <tr>
                            <th className="text-left p-2">Nome</th>
                            <th className="text-left p-2">Tipo</th>
                            <th className="text-left p-2">Valor</th>
                            <th className="text-left p-2">Piso</th>
                            <th className="text-left p-2">Teto</th>
                            <th className="text-right p-2">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={6} className="p-6 text-center opacity-70">
                                    Nenhum registro.
                                </td>
                            </tr>
                        )}
                        {items.map((s: Surcharge) => (
                            <tr key={s.id} className="border-t border-slate-800">
                                <td className="p-2">{s.name}</td>
                                <td className="p-2">{s.kind ?? "—"}</td>
                                <td className="p-2">{s.value ?? "—"}</td>
                                <td className="p-2">{s.min_value ?? "—"}</td>
                                <td className="p-2">{s.max_value ?? "—"}</td>
                                <td className="p-2 text-right">
                                    <div className="inline-flex gap-2">
                                        <Button size="sm" variant="outline" onClick={() => setEditing(s)}>
                                            Editar
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            onClick={() => mDelete.mutate(s.id)}
                                            disabled={mDelete.isPending}
                                        >
                                            Excluir
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-sm">
                <div>
                    {total ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} de ${total}` : "—"}
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                    >
                        Anterior
                    </Button>
                    <div className="px-2 py-1 border rounded">Página {page} / {totalPages}</div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                    >
                        Próxima
                    </Button>
                </div>
            </div>

            {/* Novo */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Nova Sobretaxa</DialogTitle>
                    </DialogHeader>
                    <FormSurcharge
                        onSubmit={(v) => mCreate.mutate({ ...v, rate_table: tableId })}
                        submitting={mCreate.isPending}
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancelar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Editar */}
            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Editar Sobretaxa</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <FormSurcharge
                            initial={editing}
                            onChange={setEditing}
                            onSubmit={() => mUpdate.mutate()}
                            submitting={mUpdate.isPending}
                        />
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>
                            Cancelar
                        </Button>
                        <Button onClick={() => mUpdate.mutate()} disabled={mUpdate.isPending}>
                            Salvar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function FormSurcharge({
    initial,
    onChange,
    onSubmit,
    submitting,
}: {
    initial?: Partial<Surcharge>;
    onChange?: (v: Surcharge) => void;
    onSubmit: (payload?: Partial<Surcharge>) => void;
    submitting?: boolean;
}) {
    const [f, setF] = useState<Partial<Surcharge>>({
        name: "",
        kind: "percent",
        value: "",
        min_value: "",
        max_value: "",
    });

    useEffect(() => {
        if (initial) setF(initial);
    }, [initial]);

    useEffect(() => {
        if (onChange) onChange(f as Surcharge);
    }, [f, onChange]);

    const onTxt =
        (k: keyof Surcharge) =>
            (e: React.ChangeEvent<HTMLInputElement>) =>
                setF((s) => ({ ...s, [k]: e.target.value }));

    return (
        <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label>Nome</Label>
                    <Input value={f.name ?? ""} onChange={onTxt("name")} />
                </div>
                <div>
                    <Label>Tipo</Label>
                    <select
                        className="block w-full rounded border border-slate-600 bg-slate-900/40 px-3 py-2 text-sm"
                        value={f.kind ?? "percent"}
                        onChange={(e) => setF((s) => ({ ...s, kind: e.target.value }))}
                    >
                        <option value="percent">% (percentual)</option>
                        <option value="fixed">Valor fixo</option>
                        <option value="per_kg">Por kg</option>
                        <option value="per_m3">Por m³</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
                <div>
                    <Label>Valor</Label>
                    <Input value={f.value ?? ""} onChange={onTxt("value")} />
                </div>
                <div>
                    <Label>Piso</Label>
                    <Input value={f.min_value ?? ""} onChange={onTxt("min_value")} />
                </div>
                <div>
                    <Label>Teto</Label>
                    <Input value={f.max_value ?? ""} onChange={onTxt("max_value")} />
                </div>
            </div>

            <div className="flex justify-end">
                <Button onClick={() => onSubmit(f)} disabled={!!submitting}>
                    {submitting ? "Salvando..." : "Salvar"}
                </Button>
            </div>
        </div>
    );
}
